import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  useCreateCmpProspectLeadCaptureMutation,
  useCreateCmpProspectMutation,
  useGetCmpProspectLeadCapturesQuery,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { ProspectForm } from "../ProspectForm";
import {
  ProspectFieldsInput,
  emptyProspectFormValues,
} from "../prospectFormModel";

const shouldMarkCmpProspectModuleTestData =
  process.env.NODE_ENV === "development";

const CreateProspect = () => {
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  useEffect(() => {
    setPageHeaderOverride({
      title: "Create Prospect – Company Information",
      description:
        "Use this section to create a prospect manually who has not submitted an online application.",
      withHeaderDivider: true,
    });
    return () => setPageHeaderOverride(null);
  }, [setPageHeaderOverride]);

  const {
    data: leadCaptureData,
    loading: leadCapturesLoading,
    error: leadCapturesQueryError,
  } = useGetCmpProspectLeadCapturesQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [createProspect] = useCreateCmpProspectMutation();
  const [createLeadCapture] = useCreateCmpProspectLeadCaptureMutation();

  const leadCaptures = useMemo(
    () =>
      (leadCaptureData?.getCmpProspectLeadCaptures || []).map(
        (leadCapture: { id: string; label: string }) => ({
          id: leadCapture.id,
          label: leadCapture.label,
        }),
      ),
    [leadCaptureData],
  );

  const handleCreateLeadCapture = useCallback(
    async (label: string) => {
      if (!chamberId) {
        throw new Error("Chamber information is still loading. Try again.");
      }

      const response = await createLeadCapture({
        variables: { input: { chamberId, label } },
      });
      const payload = response.data?.createCmpProspectLeadCapture;
      if (!payload?.leadCaptureSavedSuccessfully || !payload.leadCapture) {
        throw new Error("The lead capture could not be saved.");
      }

      return {
        id: payload.leadCapture.id,
        label: payload.leadCapture.label,
      };
    },
    [chamberId, createLeadCapture],
  );

  const handleSave = useCallback(
    async (data: ProspectFieldsInput) => {
      if (!chamberId) {
        throw new Error("Chamber information is still loading. Try again.");
      }

      const response = await createProspect({
        variables: {
          input: {
            chamberId,
            data,
            isCmpProspectModuleTestData: shouldMarkCmpProspectModuleTestData,
          },
        },
      });
      const payload = response.data?.createCmpProspect;
      if (!payload?.prospectSavedSuccessfully || !payload.prospect) {
        throw new Error("The prospect could not be saved.");
      }

      return payload.prospect;
    },
    [chamberId, createProspect],
  );

  return (
    <ProspectForm
      mode="create"
      initialValues={emptyProspectFormValues}
      leadCaptures={leadCaptures}
      leadCapturesLoading={!chamberId || leadCapturesLoading}
      leadCapturesError={
        leadCapturesQueryError
          ? "Lead capture options could not be loaded. You can try again or add a new lead capture."
          : undefined
      }
      cancelPath={RoutePaths.chamberAdmin_prospects_manage}
      onCreateLeadCapture={handleCreateLeadCapture}
      onSave={handleSave}
      onSaved={(prospect) => {
        navigate(RoutePaths.chamberAdmin_prospects_manage, {
          state: {
            createdProspect: prospect,
            statusMessage: "Prospect saved successfully.",
          },
        });
      }}
    />
  );
};

export default CreateProspect;
